"use client";

import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { XIcon } from "lucide-react";
import type React from "react";
import { cn } from "../../lib/utils";
import { useVisualViewportBottomInset } from "../../hooks/use-visual-viewport-inset";
import {
  BOX_SURFACE_SHADOW,
  SURFACE_BG,
  type SurfaceToken,
} from "./box-classes";
import { Button } from "./button";
import { ScrollArea } from "./scroll-area";
import type { Omittable } from "../../lib/props";

export const Sheet: typeof SheetPrimitive.Root = SheetPrimitive.Root;

export const SheetPortal: typeof SheetPrimitive.Portal = SheetPrimitive.Portal;

export function SheetTrigger(
  props: SheetPrimitive.Trigger.Props,
): React.ReactElement {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

export function SheetClose(
  props: SheetPrimitive.Close.Props,
): React.ReactElement {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

export function SheetBackdrop({
  className,
  ...props
}: SheetPrimitive.Backdrop.Props): React.ReactElement {
  return (
    <SheetPrimitive.Backdrop
      className={cn(
        "fixed inset-0 z-50 bg-black/32 backdrop-blur-sm transition-all duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0",
        className,
      )}
      data-slot="sheet-backdrop"
      {...props}
    />
  );
}

export function SheetViewport({
  className,
  side,
  variant = "default",
  clickBehind = false,
  ...props
}: SheetPrimitive.Viewport.Props & Omittable<{
  side?: "right" | "left" | "top" | "bottom";
  variant?: "default" | "inset" | "bare";
  /** Lets pointer events pass through the viewport to the page beneath —
   *  the viewport is a `fixed inset-0` element, so without this every click
   *  outside the popup dies here even when the sheet is non-modal. */
  clickBehind?: boolean;
}>): React.ReactElement {
  // Keyboard compensation: the layout viewport this `fixed inset-0` element
  // sizes against does not shrink for the software keyboard on iOS Safari
  // (or any `resizes-visual` browser); the measured overlap becomes bottom
  // padding so bottom-anchored content stays above the keyboard.
  const keyboardInset = useVisualViewportBottomInset();
  return (
    <SheetPrimitive.Viewport
      className={cn(
        "fixed inset-0 z-50 grid",
        side === "bottom" && "grid grid-rows-[1fr_auto] pt-12",
        side === "top" && "grid grid-rows-[auto_1fr] pb-12",
        side === "left" && "flex justify-start",
        side === "right" && "flex justify-end",
        variant === "inset" && "sm:p-4",
        clickBehind && "pointer-events-none",
        className,
      )}
      data-slot="sheet-viewport"
      style={keyboardInset > 0 ? { paddingBottom: keyboardInset } : undefined}
      {...props}
    />
  );
}

export function SheetPopup({
  className,
  children,
  showCloseButton = true,
  showBackdrop = true,
  side = "right",
  variant = "default",
  surface = "popover",
  closeProps,
  portalProps,
  ...props
}: SheetPrimitive.Popup.Props & Omittable<{
  showCloseButton?: boolean;
  /**
   * The surface the sheet paints. The default reads as a floating panel,
   * like a dialog. Name a quieter surface for a sheet that is the mobile
   * presentation of an inline region — a nav drawer holding the same chrome
   * the desktop layout renders in a sidebar — so it paints the same surface in
   * both places instead of lifting off the page only on mobile.
   */
  surface?: SurfaceToken;
  /** A non-modal sheet (`<Sheet modal={false}>`) that must leave the page
   *  visible and clickable turns the scrim off. The backdrop is a real
   *  viewport-covering element, so `modal={false}` alone still dims and
   *  intercepts. */
  showBackdrop?: boolean;
  side?: "right" | "left" | "top" | "bottom";
  /**
   * `bare` strips the popup's own chrome — background, border, shadow and
   * hairline — leaving a positioned, animated container. For a sheet whose
   * children already carry their own surfaces, so the sheet does not paint a
   * slab behind them.
   */
  variant?: "default" | "inset" | "bare";
  closeProps?: SheetPrimitive.Close.Props;
  portalProps?: SheetPrimitive.Portal.Props;
}>): React.ReactElement {
  return (
    <SheetPortal {...portalProps}>
      {showBackdrop && <SheetBackdrop />}
      {/* A backdrop-less sheet is a side panel over a page that stays live:
          the viewport goes click-through and the popup re-arms itself, so a
          click beside the panel lands on the page instead of dying on the
          full-viewport container. */}
      <SheetViewport clickBehind={!showBackdrop} side={side} variant={variant}>
        <SheetPrimitive.Popup
          className={cn(
            "pointer-events-auto relative flex max-h-full min-h-0 w-full min-w-0 flex-col not-dark:bg-clip-padding outline-none transition-[opacity,translate] duration-200 ease-in-out will-change-transform before:pointer-events-none before:absolute before:inset-0 before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:opacity-0 data-starting-style:opacity-0 max-sm:before:hidden dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
            // Before the side/variant lines so `bare` still out-merges the
            // surface's fill and shadow away.
            SURFACE_BG[surface],
            BOX_SURFACE_SHADOW[surface],
            side === "bottom" &&
              // Meets the screen edge → owes the home-indicator safe area.
              "row-start-2 border-t pb-[env(safe-area-inset-bottom,0px)] data-ending-style:translate-y-8 data-starting-style:translate-y-8",
            side === "top" &&
              "border-b data-ending-style:-translate-y-8 data-starting-style:-translate-y-8",
            side === "left" &&
              "w-[calc(100%-(--spacing(12)))] max-w-md border-e data-ending-style:-translate-x-8 data-starting-style:-translate-x-8",
            side === "right" &&
              "col-start-2 w-[calc(100%-(--spacing(12)))] max-w-md border-s data-ending-style:translate-x-8 data-starting-style:translate-x-8",
            variant === "inset" &&
              "before:hidden sm:rounded-xl sm:border sm:before:rounded-[calc(var(--radius-xl)-1px)] sm:**:data-[slot=sheet-footer]:rounded-b-[calc(var(--radius-xl)-1px)]",
            variant === "bare" &&
              "border-0 bg-transparent shadow-none before:hidden",
            className,
          )}
          data-slot="sheet-popup"
          {...props}
        >
          {children}
          {showCloseButton && (
            <SheetPrimitive.Close
              aria-label="Close"
              className="absolute end-2 top-2"
              render={<Button size="icon" variant="ghost" />}
              {...closeProps}
            >
              <XIcon />
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Popup>
      </SheetViewport>
    </SheetPortal>
  );
}

export function SheetHeader({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex flex-col gap-2 p-6 in-[[data-slot=sheet-popup]:has([data-slot=sheet-panel])]:pb-3 max-sm:pb-4",
      className,
    ),
    "data-slot": "sheet-header",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function SheetFooter({
  className,
  variant = "default",
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  variant?: "default" | "bare";
}>): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex flex-col-reverse gap-2 px-6 sm:flex-row sm:justify-end",
      // Without a SheetPanel the body has no bottom gutter of its own; the
      // margin restores the gap and is inert when a panel is present.
      "in-[[data-slot=sheet-popup]:not(:has([data-slot=sheet-panel]))]:mt-4",
      variant === "default" && "border-t py-4",
      variant === "bare" &&
        "in-[[data-slot=sheet-popup]:has([data-slot=sheet-panel])]:pt-3 pt-4 pb-6",
      className,
    ),
    "data-slot": "sheet-footer",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function SheetTitle({
  className,
  ...props
}: SheetPrimitive.Title.Props): React.ReactElement {
  return (
    <SheetPrimitive.Title
      className={cn(
        "font-heading font-semibold text-xl leading-none",
        className,
      )}
      data-slot="sheet-title"
      {...props}
    />
  );
}

export function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props): React.ReactElement {
  return (
    <SheetPrimitive.Description
      className={cn("text-muted-foreground text-sm", className)}
      data-slot="sheet-description"
      {...props}
    />
  );
}

export function SheetPanel({
  className,
  scrollFade = true,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  scrollFade?: boolean;
}>): React.ReactElement {
  const defaultProps = {
    className: cn(
      "p-6 in-[[data-slot=sheet-popup]:has([data-slot=sheet-header])]:pt-1 in-[[data-slot=sheet-popup]:has([data-slot=sheet-footer]:not(.border-t))]:pb-1",
      className,
    ),
    "data-slot": "sheet-panel",
  };

  return (
    <ScrollArea scrollFade={scrollFade}>
      {useRender({
        defaultTagName: "div",
        props: mergeProps<"div">(defaultProps, props),
        render,
      })}
    </ScrollArea>
  );
}
